import React, { useMemo, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { AppGroup } from "../../utils/types";
import { SelectedGroupDetails } from "./SelectedGroupDetails";

export type GroupRow = AppGroup;

type GroupsTableProps = {
  rows: GroupRow[];
  height?: number;
};

const ROW_HEIGHT = 56;
const OVERSCAN = 6;

export function GroupsTable({ rows, height = 520 }: GroupsTableProps) {
  const [scrollTop, setScrollTop] = useState(0);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

  const selectedIndex = useMemo(
    () => rows.findIndex((group) => group.id === selectedGroupId),
    [rows, selectedGroupId],
  );

  const selectedGroup = useMemo(
    () => rows.find((group) => group.id === selectedGroupId) || null,
    [rows, selectedGroupId],
  );

  const detailsHeight = useMemo(() => {
    if (!selectedGroup) {
      return 0;
    }

    // Estimativa para manter o painel sem scroll interno.
    return 220 + (selectedGroup.participants?.length || 0) * 26;
  }, [selectedGroup]);

  const totalHeight =
    rows.length * ROW_HEIGHT + (selectedIndex >= 0 ? detailsHeight : 0);

  const getRowTop = (rowIndex: number) => {
    const baseTop = rowIndex * ROW_HEIGHT;

    if (selectedIndex >= 0 && rowIndex > selectedIndex) {
      return baseTop + detailsHeight;
    }

    return baseTop;
  };

  const startIndex = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
  const endIndex = Math.min(
    rows.length,
    Math.ceil((scrollTop + height) / ROW_HEIGHT) + OVERSCAN,
  );

  const visibleRows = useMemo(
    () => rows.slice(startIndex, endIndex),
    [rows, startIndex, endIndex],
  );

  const getGroupDisplayName = (group: GroupRow) => {
    const responsible = group.participants.find((participant) => participant.isResponsible);
    if (responsible?.name) {
      return `Grupo de ${responsible.name}`;
    }
    if (group.createdBy) {
      return group.createdBy;
    }
    return `Grupo ${group.id.slice(0, 6)}`;
  };

  const getGroupCity = (group: GroupRow) => {
    return group.meetingLocation.neighborhood || group.location || "Nao informado";
  };

  if (!rows.length) {
    return (
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography color="text.secondary">Nenhum grupo encontrado.</Typography>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ overflowX: "auto" }}>
      <Box
        role="row"
        sx={{
          minWidth: 760,
          display: "grid",
          gridTemplateColumns: "160px 2fr 140px 1.4fr",
          px: 2,
          py: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          backgroundColor: "#f7f8fb",
          fontWeight: 700,
          position: "sticky",
          top: 0,
          zIndex: 1,
          gap: 2,
        }}
      >
        <Typography variant="body2" fontWeight={700}>ID</Typography>
        <Typography variant="body2" fontWeight={700}>Nome do grupo</Typography>
        <Typography variant="body2" fontWeight={700}>Integrantes</Typography>
        <Typography variant="body2" fontWeight={700}>Cidade</Typography>
      </Box>

      <Box
        onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
        sx={{
          minWidth: 760,
          height,
          overflowY: "auto",
          position: "relative",
        }}
      >
        <Box sx={{ height: totalHeight, position: "relative" }}>
          {visibleRows.map((group, index) => {
            const rowIndex = startIndex + index;

            return (
              <Box
                key={group.id}
                role="row"
                onClick={() =>
                  setSelectedGroupId((current) =>
                    current === group.id ? null : group.id,
                  )
                }
                sx={{
                  position: "absolute",
                  top: getRowTop(rowIndex),
                  left: 0,
                  right: 0,
                  height: ROW_HEIGHT,
                  display: "grid",
                  gridTemplateColumns: "160px 2fr 140px 1.4fr",
                  alignItems: "center",
                  px: 2,
                  gap: 2,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  backgroundColor:
                    selectedGroupId === group.id
                      ? "#eaf2ff"
                      : rowIndex % 2 === 0
                        ? "#ffffff"
                        : "#fcfcfe",
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor:
                      selectedGroupId === group.id ? "#eaf2ff" : "#f2f6ff",
                  },
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {group.id}
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {getGroupDisplayName(group)}
                </Typography>
                <Typography variant="body2">{group.participants.length}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {getGroupCity(group)}
                </Typography>
              </Box>
            );
          })}

          {selectedGroup && selectedIndex >= 0 && (
            <SelectedGroupDetails
              group={selectedGroup}
              top={getRowTop(selectedIndex) + ROW_HEIGHT}
              minHeight={detailsHeight - 8}
            />
          )}
        </Box>
      </Box>
    </Paper>
  );
}

export default GroupsTable;
